import { Arr, Burden, Logger } from '@deqo/foundation';
import { ClientRef, Connection, EventSourceBase, Podopolis, PresenceEvents, RelayEvents, TopicEvents } from '@deqo/podopolis';
import { injectable } from 'inversify';
import { unixTime } from '../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../api/ApiClient';
import { getPermitToken } from '../../api/ApiEndpoints';
import { v2 } from '../../api/ApiInterfaceV2';
import { AppLocation } from '../AppLocation';
import { AccountModel } from '../account/AccountModel';
import { Hangout, HangoutClients, HangoutLocations, HangoutUsers } from './Hangout';
import { HangoutClient, HangoutClientStore } from './HangoutClient';
import { HangoutEvents } from './HangoutEvents';
import { HangoutLocation, HangoutLocationStore } from './HangoutLocation';
import { HangoutPodSession } from './HangoutPodSession';
import { HangoutTopic } from './HangoutTopic';
import { HangoutUser, HangoutUserStore } from './HangoutUser';

const logger = Logger.get('app.hangout');

@injectable()
export class DefaultHangout extends EventSourceBase<HangoutEvents> implements Hangout {

    private readonly $clients = new HangoutClientStore();
    private readonly $users = new HangoutUserStore();
    private readonly $locations = new HangoutLocationStore();
    private readonly tracker = new Map<string, string>();
    private readonly topics = new Set<HangoutTopic>();

    private activePod = null as HangoutPodSession;

    constructor(
        private account: AccountModel,
        private api: ApiClient,
        private myLocation: AppLocation,
        private podopolis: Podopolis,
    ) {
        super();
        this.burden(
            this.myLocation.observe(this.updateMyLocation),
            this.podopolis.onActiveConnection(this.onConnect),
            () => Burden.resolveAll(Array.from(this.topics)),
        );
    }


    public get self() {
        return this.$clients.get(this.activePod?.getThisClient()?.id) ?? null;
    }

    public get clients() {
        return this.$clients as HangoutClients;
    }

    public get locations() {
        return this.$locations as HangoutLocations;
    }

    public get users() {
        return this.$users as HangoutUsers;
    }

    public get ready() {
        return !!this.activePod;
    }

    public getPod() {
        return this.activePod;
    }

    public waitForReady(): Promise<void> {
        if (this.ready) return Promise.resolve();
        return new Promise((rs) => {
            this.once('ready', () => rs(undefined));
        });
    }

    public subscribeTo(topic: string): HangoutTopic {
        const hts = new HangoutTopic(topic, this, () => this.onReleaseTopic(hts));
        this.topics.add(hts);
        if (this.activePod) {
            this.activePod.subscribeTopic(topic);
        }
        return hts;
    }

    public sendRelay(to: string | '*', type: string, data: any) {
        if (this.activePod) {
            this.activePod.sendRelay(to, [type, data]);
        }
    }

    private onConnect = async (conn: Connection) => {
        const { permitToken } = await this.api.exec(getPermitToken(this.account.podId, 'account'));
        const pod = new HangoutPodSession(await conn.join(this.account.podId, permitToken));
        await pod.start();
        logger.log('Hangout connected');
        this.onInit(pod);
    };

    private onInit = (pod: HangoutPodSession) => {

        this.activePod = pod;

        pod.on('clientJoin', this.onClientJoin);
        pod.on('clientLeave', this.onClientLeave);
        pod.on('clientUpdate', this.onClientUpdate);
        pod.on('relay', this.onRelay);
        pod.on('topicBroadcast', this.onTopicBroadcast);
        pod.on('topicUpdate', this.onTopicUpdate);
        pod.on('destroy', this.onCleanUp);

        // If we have active topic subscriptions, re-subscribe...
        for (const topic of new Set(Array.from(this.topics).map(x => x.name))) {
            pod.subscribeTopic(topic);
        }

        // Initialize all existing clients
        const clients = pod.getClients();
        for (const ref of clients) {
            this.registerClient(ref);
            this.updateClientLocation(ref, this.getClientLocationFromPod(ref.id));
        }

        // Add "external" client
        this.registerClient({ id: 'SYSTEM/$', userId: 'SYSTEM', instanceId: '$' });

        this.updateMyLocation();
        logger.log('Hangout initialized');

        this.emit('ready', []);
    };

    private onCleanUp = () => {
        this.activePod = null;
        this.$locations.reset();
        this.$clients.reset();
        this.$users.forEach(u => u.mutate(x => x.clients.clear()));
    };

    private onRelay = ([ref, data, _to]: RelayEvents['relay']) => {
        const sender = this.$clients.get(ref.id);
        if (sender && Array.isArray(data) && data.length >= 2) {
            const [type, body] = data;
            if (logger.enabled) logger.log(type, 'direct from', ref.id, 'with', JSON.stringify(body));
            this.emit('clientRelay', [sender, type, body]);
        }
    };

    private onTopicBroadcast = ([topic, data, ref]: TopicEvents['topicBroadcast']) => {
        const sender = this.$clients.get(ref.id);
        if (sender && Array.isArray(data) && data.length >= 2) {
            const [type, body] = data;
            if (logger.enabled) logger.log(type, 'via topic', topic, 'from', ref.id, 'with', JSON.stringify(body));
            this.emit('topicBroadcast', [sender, topic, type, body]);
        }
    };

    private onTopicUpdate = ([topic, data, client]: TopicEvents['topicUpdate']) => {
        const sender = this.$clients.get(client.id);
        if (sender) {
            if (logger.enabled) logger.log(topic, 'patched');
            this.emit('topicUpdate', [sender, topic, data]);
        }
    };

    private onClientJoin = (ref: ClientRef) => {
        logger.log(ref.id, 'joined hangout');
        this.registerClient(ref);
        this.emit('clientJoin', this.resolveClient(ref.id));
    };

    private onClientLeave = (ref: ClientRef) => {
        logger.log(ref.id, 'left hangout');
        const client = this.resolveClient(ref.id);
        this.updateClientLocation(ref, null);
        this.unregisterClient(ref);
        this.emit('clientLeave', client);
    };

    private onClientUpdate = ([ref, _profile, affectedKeys]: PresenceEvents['clientUpdate']) => {
        if (affectedKeys.includes('location')) {
            logger.log(ref.id, 'moved to', _profile.location || '?');
            this.updateClientLocation(ref, this.getClientLocationFromPod(ref.id));
        }
        this.emit('clientUpdate', [this.resolveClient(ref.id), affectedKeys]);
    };

    private onReleaseTopic = (hts: HangoutTopic) => {
        // Remove
        this.topics.delete(hts);
        // If pod active, forget if we have no more subs for this topic
        const hasOthers = !!Array.from(this.topics).find(x => x.name === hts.name);
        if (hasOthers || !this.activePod) return;
        this.activePod.forgetTopic(hts.name);
    };

    private resolveClient(clientId: string) {
        if (!clientId) return null;
        return this.$clients.get(clientId) ?? this.allocateClient(clientId);
    }
    private allocateClient(clientId: string) {
        const client = new HangoutClient(clientId);
        this.$clients.mutate(x => x.add(client));
        return client;
    }

    private releaseClient(client: HangoutClient) {
        this.$clients.mutate(x => x.delete(client));
    }

    private getClientLocationFromPod(clientId: string) {
        return this.activePod?.getClientProfileValue(clientId, 'location') ?? null;
    }

    private resolveLocation(locationId: string) {
        if (!locationId) return null;
        return this.$locations.get(locationId) ?? this.allocateLocation(locationId);
    }

    private allocateLocation(locationId: string) {
        const location = new HangoutLocation(locationId);
        this.$locations.mutate(x => x.add(location));
        return location;
    }

    private resolveUser(userId: string) {
        if (!userId) return null;
        return this.users.get(userId) ?? this.allocateUser(userId);
    }

    private allocateUser(userId: string) {
        const user = new HangoutUser(userId);
        this.$users.mutate(x => x.add(user));
        // Dispatch fetch for user data unless its external/system
        if (userId !== 'SYSTEM') {
            this.api.exec(v2.users.info(this.account.id, userId))
                .then(data => user.mutate(data));
        }
        else {
            user.mutate({ name: 'System' });
        }
        return user;
    }

    private findLatestUserLocation(user: HangoutUser) {
        const lastSeenClient = Arr.first(Arr.sortBy(Array.from(user.clients), 'seenAt', 'desc'));
        return lastSeenClient?.location ?? null;
    }

    private registerClient(ref: ClientRef) {
        const client = this.resolveClient(ref.id);
        const user = this.resolveUser(ref.userId);
        client.mutate({ user });
        user.mutate(x => x.clients.add(client));
    }

    private unregisterClient(ref: ClientRef) {
        const client = this.resolveClient(ref.id);
        const user = this.resolveUser(ref.userId);
        client.mutate({ user: null });
        user.mutate(x => x.clients.delete(client));
        this.releaseClient(client);
        // For now, don't release users...
    }

    private updateClientLocation(ref: ClientRef, nextPath: string) {
        const prevPath = this.tracker.get(ref.id);
        const client = this.resolveClient(ref.id);
        const user = this.resolveUser(ref.userId);
        // If location is different, update client / location entries
        if (prevPath !== nextPath) {
            // Obtain client/user objects
            // Obtain prev/next location objects (might be null)
            const prev = this.resolveLocation(prevPath);
            const next = this.resolveLocation(nextPath);
            // If prev exists, unlink
            if (prev) {
                prev.mutate(x => x.clients.delete(client));
            }
            // If next exists, link
            if (next) {
                next.mutate(x => x.clients.add(client));
            }
            // Update the client & tracker
            client.mutate({ location: next, seenAt: unixTime() });
            this.tracker.set(client.id, next?.path ?? null);
        }
        // If it's the same, just update the seenAt
        else {
            client.mutate({ seenAt: unixTime() });
        }
        // Even if the location didn't change, the "latest" location of the user might have now
        {
            const prev = user.location;
            const next = this.findLatestUserLocation(user);
            if (prev !== next) {
                user.mutate({ location: next });
                if (prev) {
                    prev.mutate(x => x.users.delete(user));
                }
                if (next) {
                    next.mutate(x => x.users.add(user));
                }
            }
        }
    }

    public updateMyLocation = () => {
        const pod = this.activePod;
        const at = this.myLocation.getState();
        if (pod && at) {
            const location = at.map(x => `${x.name}:${x.value}`).join('/');
            pod.patchProfile({ location });
            this.updateClientLocation(pod.getThisClient(), location);
        }
    };
}
